import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { SettlementTypeEnums } from 'src/app/enum/settlement-type-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { InvoiceModel } from 'src/app/models/invoicing/invoice.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { SalesRemissionSelectionTableModel } from 'src/app/models/tables/inventory/sales-remission-selection-table.model';
import { InvoiceDetailService } from 'src/app/services/invoicing/invoice-detail.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-settlement',
  templateUrl: './settlement.component.html',
  styleUrls: ['./settlement.component.scss'],
  standalone: false,
})
export class SettlementComponent implements OnInit {

  ascendantSalesRemission: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: '',
      color: ActionColor.PRIMARY,
      action: 'bulk-create'
    },
  ];
  columnSortSalesRemission: string | null = null;
  companyId: number = 0;
  conceptInvoicingId: number = 0;
  data: PagedResponseModel<SalesRemissionSelectionTableModel> = new PagedResponseModel<SalesRemissionSelectionTableModel>();
  filters: ColumnResponseModel[] = [];
  formSettlement: FormGroup = new FormGroup({});
  headers: ColumnResponseModel[] = [];
  invoiceId: number = 0;
  limitSalesRemission: number = 10;
  listConceptInvoicing: any[] = [];
  output$: Subject<any>;
  pageSalesRemission: number = 1;
  settlementType: number = 0;
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private companyService: CompanyService,
    private invoiceDetailService: InvoiceDetailService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef) {
    this.validateForm();
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.headers = inputs.headers ?? [];
      this.data = inputs.data ?? [];
      this.companyId = inputs.companyId;
      this.invoiceId = inputs.invoiceId;
      this.settlementType = inputs.settlementType;
      this.filters = inputs.filters ?? [];
    });
    await this.getlistConceptInvoicing();
    await this.getCompanyId();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async createDetail(salesRemissionIds: number[]) {
    try {
      const invoice = await firstValueFrom(this.invoiceDetailService.createDetailFromSalesRemissionAsync(this.invoiceId,
        this.conceptInvoicingId, salesRemissionIds));
      this.toastHandlerService.showToast('success', 'Los detalles de la factura se generaron correctamente.');
      if (invoice) { this.close(invoice); }
    } catch (error) {
      console.error('Error al crear el detalle de la factura.', error);
    }
  }

  close(invoice: InvoiceModel) {
    const data: ModalObjectType = {};
    data.params = { invoice: invoice };
    this.ref.close(data);
  }

  async getCompanyId() {
    try {
      if (!this.companyId) { return; }
      const company = await firstValueFrom(this.companyService.getCompanyById(this.companyId));

      switch (this.settlementType) {
        case SettlementTypeEnums.newSale:
          if (company.conceptInvoicingIdSale == null) {
            this.toastHandlerService.showToast('warning', 'Falta definir el concepto de venta nuevo predeterminado en Compañías.');
            return;
          }
          this.formSettlement.controls["conceptInvoicingId"].patchValue(company.conceptInvoicingIdSale ?? null);
          break;
        case SettlementTypeEnums.usedSale:
          if (company.conceptInvoicingIdSale == null) {
            this.toastHandlerService.showToast('warning', 'Falta definir el concepto de venta usado predeterminado en Compañías.');
            return;
          }
          this.formSettlement.controls["conceptInvoicingId"].patchValue(company.conceptInvoicingIdReplacement ?? null);
          break;
      }
    } catch (error) {
      console.error('Error al obtener la información de la compañía.', error);
    }
  }

  async getlistConceptInvoicing() {
    try {
      this.listConceptInvoicing = await firstValueFrom(this.invoiceDetailService.getAllConceptInvoicingForSelectAsync());
    } catch (error) {
      console.error('Error al obtener la lista de conceptos.', error);
    }
  }

  async onAction(event: { action: string; item?: SalesRemissionSelectionTableModel; selectedItems?: number[] }) {
    const { action, selectedItems } = event;
    if (action !== 'bulk-create') {
      console.warn('Acción no reconocida o no implementada.', action);
      return;
    }
    const conceptInvoicingId = this.formSettlement.get('conceptInvoicingId')?.value;
    if (!conceptInvoicingId) {
      this.toastHandlerService.showToast('warning', 'Seleccione un concepto de facturación válido antes de continuar.');
      return;
    }
    this.conceptInvoicingId = conceptInvoicingId;
    const confirmation = await Swal.fire({
      text: '¿Está seguro de que desea generar los detalles de la factura a partir de las remisiones de venta seleccionadas?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, generar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmation.isConfirmed) return;
    try {
      await this.createDetail(selectedItems!);
    } catch (error) {
      console.error('Error al crear los detalles de la factura.', error);
    }
  }

  onPageChangeEmit(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onPageChange', payload: event });
  }

  onFilterChangeEmit(event: { filters: any }) {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onFilterChange', payload: event });
  }

  validateForm(): void {
    this.formSettlement = this.formBuilder.group({
      conceptInvoicingId: [null, Validators.required],
    });
  }
}
